import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  SKIN_THEMES
} from "../layouts/constants";

const DEFAULT_CENTER = [20.5937, 78.9629];

const POLL_MS = 8000;
const POINT_MOVE_MS = 850;

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find(s => s.id === skinId) || SKIN_THEMES[0];
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");

  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }

  return `${base}${path}`;
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function isNum(v) {
  return Number.isFinite(Number(v));
}

function km(m) {
  return (Number(m || 0) / 1000).toFixed(1);
}

function kph(mps) {
  return Math.round(Number(mps || 0) * 3.6);
}

function duration(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "—";

  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;

  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function pointTime(p) {
  const t = Date.parse(p.ts || p.recorded_at || p.created_at || "");
  return Number.isFinite(t) ? t : 0;
}

function normalizePoints(points = []) {
  const seen = new Set();

  return points
    .map(p => ({
      ...p,
      id: Number(p.id || 0),
      lat: toNum(p.lat),
      lng: toNum(p.lng),
      speed_mps: toNum(p.speed_mps) || 0,
      heading_deg: toNum(p.heading_deg) || 0,
      ts: p.ts || p.recorded_at || p.created_at || "",
      _time: pointTime(p)
    }))
    .filter(p => isNum(p.lat) && isNum(p.lng))
    .sort((a, b) => {
      if (a._time !== b._time) return a._time - b._time;
      return a.id - b.id;
    })
    .filter(p => {
      const key = `${p.lat.toFixed(7)},${p.lng.toFixed(7)},${p._time}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function vehicleSvg(rotation = 0) {
  return `
    <div style="transform: rotate(${rotation}deg); transform-origin:center;">
      <svg viewBox="0 0 64 32" width="52" height="52" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="10" width="30" height="12" rx="2" fill="#111827" />
        <path d="M34 10h12l6 6v6H34z" fill="#111827" />
        <circle cx="14" cy="26" r="4" fill="#1e293b" />
        <circle cx="42" cy="26" r="4" fill="#1e293b" />
      </svg>
    </div>
  `;
}

function vehicleIcon(rotation = 0) {
  return L.divIcon({
    className: "escort-vehicle-icon",
    html: vehicleSvg(rotation),
    iconSize: [52, 52],
    iconAnchor: [26, 26]
  });
}

function stoppedIcon() {
  return L.divIcon({
    className: "stopped-marker",
    html: `
      <div style="
        background:#ef4444;
        color:white;
        width:32px;
        height:32px;
        border-radius:50%;
        display:flex;
        align-items:center;
        justify-content:center;
        font-weight:bold;
        font-size:16px;
        border:3px solid white;
        box-shadow:0 4px 8px rgba(0,0,0,0.3);
      ">S</div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16]
  });
}

export default function SentinelEscortLiveTracking() {
  const navigate = useNavigate();
  const location = useLocation();
  const skin = useSkin();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const memberId = params.get("member_id") || "";
  const runId = params.get("run_id") || "";

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    user_map: ""
  });

  const routePage = useMemo(
    () =>
      dashboardData.user_map === "google"
        ? "/route-master"
        : "/route-master1",
    [dashboardData]
  );

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const errorShownRef = useRef(false);

  const mapRef = useRef(null);
  const mapBoxRef = useRef(null);

  const latestMarkerRef = useRef(null);
  const startMarkerRef = useRef(null);
  const stopMarkerRef = useRef(null);
  const stoppedMarkerRef = useRef(null);
  const polylineRef = useRef(null);

  const pollRef = useRef(null);
  const animationRef = useRef(null);
  const fittedRef = useRef(false);

  const latestLatLngRef = useRef(null);
  const lastPointIdRef = useRef(null);
  const moveQueueRef = useRef([]);
  const isMovingRef = useRef(false);

  const [followLive, setFollowLive] = useState(true);
  const followRef = useRef(true);

  const [status, setStatus] = useState({
    label: "—",
    className: "bg-slate-200 text-slate-700"
  });

  const [meta, setMeta] = useState({
    title: "—",
    from: "—",
    to: "—",
    vehicle: "—",
    lastTs: "—",
    distance: "0",
    speed: "0",
    duration: "—",
    points: "0"
  });

  const apiUrl = useMemo(() => {
    const q = new URLSearchParams({
      member_id: memberId,
      limit: "2000",
      include_segments: "1"
    });

    if (runId) {
      q.set("run_id", runId);
    }

    return api(`/api/sentinel_escort/live/tracking?${q.toString()}`);
  }, [memberId, runId]);

  const fetchDashboard = useCallback(async () => {
    try {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken");

      const res = await fetch(api("/api/dashboard/admin/dashboard"), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const json = await res.json();
      setDashboardData(json?.data?.companyDetails || {});
    } catch (e) {
      setPageError(e.message);
      toast.error(e.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const setRunStatus = useCallback(s => {
    s = String(s || "").toLowerCase();

    if (s === "active") {
      setStatus({
        label: "Active",
        className: "bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]"
      });
    } else if (s === "stopped" || s === "abandoned") {
      setStatus({
        label: s.charAt(0).toUpperCase() + s.slice(1),
        className: "bg-[var(--red-faint)] border border-[var(--red-border)] text-[var(--red)]"
      });
    } else if (s === "planned") {
      setStatus({
        label: "Planned",
        className: "bg-[rgba(59,130,246,0.12)] border border-[rgba(59,130,246,0.28)] text-[#3b82f6]"
      });
    } else {
      setStatus({
        label: s || "—",
        className: "bg-[var(--surface)] border border-[var(--surface-border)] text-[var(--text-muted)]"
      });
    }
  }, []);

  const moveQueue = useCallback(() => {
    if (isMovingRef.current) return;

    const map = mapRef.current;
    const marker = latestMarkerRef.current;

    if (!map || !marker) return;
    if (!moveQueueRef.current.length) return;

    isMovingRef.current = true;

    const point = moveQueueRef.current.shift();
    const from = marker.getLatLng();
    const to = L.latLng(point.lat, point.lng);

    marker.setIcon(vehicleIcon(point.heading_deg || 0));
    marker.setTooltipContent(point.ts || "Live");

    const start = performance.now();

    const animate = now => {
      const t = Math.min((now - start) / POINT_MOVE_MS, 1);
      const ease = 1 - Math.pow(1 - t, 3);

      const lat = from.lat + (to.lat - from.lat) * ease;
      const lng = from.lng + (to.lng - from.lng) * ease;

      marker.setLatLng([lat, lng]);

      if (followRef.current) {
        map.panTo([lat, lng], {
          animate: false
        });
      }

      if (t < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        latestLatLngRef.current = to;
        isMovingRef.current = false;
        moveQueue();
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, []);

  const updateMap = useCallback(payload => {
    const map = mapRef.current;
    if (!map) return [];

    const run = payload.run || {};
    const rawPoints = Array.isArray(payload.points) ? payload.points : [];
    const points = normalizePoints(rawPoints);

    const coords = points.map(p => [p.lat, p.lng]);

    if (coords.length >= 2) {
      if (!polylineRef.current) {
        polylineRef.current = L.polyline(coords, {
          color: "#0284c7",
          weight: 5,
          opacity: 0.85,
          lineJoin: "round",
          lineCap: "round",
          smoothFactor: 1
        }).addTo(map);
      } else {
        polylineRef.current.setLatLngs(coords);
      }
    }

    if (isNum(run.start_lat) && isNum(run.start_lng)) {
      const startLatLng = L.latLng(run.start_lat, run.start_lng);

      if (!startMarkerRef.current) {
        startMarkerRef.current = L.circleMarker(startLatLng, {
          radius: 8,
          color: "#22c55e",
          fillColor: "#22c55e",
          fillOpacity: 0.9
        })
          .addTo(map)
          .bindTooltip(run.from_name || "Start");
      } else {
        startMarkerRef.current.setLatLng(startLatLng);
      }
    }

    if (isNum(run.end_lat) && isNum(run.end_lng)) {
      const endLatLng = L.latLng(run.end_lat, run.end_lng);

      if (!stopMarkerRef.current) {
        stopMarkerRef.current = L.circleMarker(endLatLng, {
          radius: 8,
          color: "#ef4444",
          fillColor: "#ef4444",
          fillOpacity: 0.9
        })
          .addTo(map)
          .bindTooltip(run.to_name || "End");
      } else {
        stopMarkerRef.current.setLatLng(endLatLng);
      }
    }

    if (points.length) {
      if (!latestMarkerRef.current) {
        const firstPoint = points[0];
        const firstLatLng = L.latLng(firstPoint.lat, firstPoint.lng);

        latestMarkerRef.current = L.marker(firstLatLng, {
          icon: vehicleIcon(firstPoint.heading_deg || 0),
          zIndexOffset: 999
        })
          .addTo(map)
          .bindTooltip(firstPoint.ts || "Live");

        latestLatLngRef.current = firstLatLng;
        lastPointIdRef.current = firstPoint.id;
      }

      const newPoints = points.filter(p => {
        if (!lastPointIdRef.current) return true;
        return Number(p.id) > Number(lastPointIdRef.current);
      });

      if (newPoints.length) {
        lastPointIdRef.current = newPoints[newPoints.length - 1].id;

        moveQueueRef.current.push(...newPoints);
        moveQueue();
      }
    }

    const latestPoint = points[points.length - 1];

    const isStopped = ["stopped", "abandoned"].includes(
      String(run.status || "").toLowerCase()
    );

    if (isStopped && latestPoint) {
      const stoppedLatLng = L.latLng(latestPoint.lat, latestPoint.lng);

      if (!stoppedMarkerRef.current) {
        stoppedMarkerRef.current = L.marker(stoppedLatLng, {
          icon: stoppedIcon(),
          zIndexOffset: 1000
        })
          .addTo(map)
          .bindTooltip("Stopped Here");
      } else {
        stoppedMarkerRef.current.setLatLng(stoppedLatLng);
      }
    } else if (stoppedMarkerRef.current) {
      map.removeLayer(stoppedMarkerRef.current);
      stoppedMarkerRef.current = null;
    }

    if (!fittedRef.current && coords.length >= 2) {
      const bounds = L.latLngBounds(coords);

      if (isNum(run.start_lat) && isNum(run.start_lng)) {
        bounds.extend([run.start_lat, run.start_lng]);
      }

      if (isNum(run.end_lat) && isNum(run.end_lng)) {
        bounds.extend([run.end_lat, run.end_lng]);
      }

      if (bounds.isValid()) {
        map.fitBounds(bounds.pad(0.15));
        fittedRef.current = true;
      }
    }

    return points;
  }, [moveQueue]);

  const refresh = useCallback(async () => {
    if (!memberId) {
      toast.error("Member ID is required");
      return;
    }

    try {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken");

      const res = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json.message || "API error");
      }

      // Reset error flag on success
      errorShownRef.current = false;

      const payload = json.data;
      const run = payload.run || {};
      const points = updateMap(payload);
      const latest = points[points.length - 1] || payload.latest || {};

      setMeta({
        title: run.title || "—",
        from: run.from_name || "—",
        to: run.to_name || "—",
        vehicle: run.vehicle || "—",
        lastTs: latest.ts || "—",
        distance: km(run.distance_m),
        speed: String(kph(latest.speed_mps)),
        duration: run.start_time
          ? duration(
              Date.parse(run.end_time || new Date()) -
                Date.parse(run.start_time)
            )
          : "—",
        points: String(points.length || 0)
      });

      setRunStatus(run.status);
    } catch (err) {
      console.error("Refresh error:", err);
      
      // Only show toast for errors, not for normal status updates
      if (!errorShownRef.current) {
        toast.error(err.message || "Failed to fetch tracking data");
        errorShownRef.current = true;
      }
    }
  }, [apiUrl, memberId, updateMap, setRunStatus]);

  const fitTrack = () => {
    const map = mapRef.current;
    if (!map) return;

    const group = L.featureGroup([]);

    if (latestMarkerRef.current) group.addLayer(latestMarkerRef.current);
    if (startMarkerRef.current) group.addLayer(startMarkerRef.current);
    if (stopMarkerRef.current) group.addLayer(stopMarkerRef.current);
    if (stoppedMarkerRef.current) group.addLayer(stoppedMarkerRef.current);
    if (polylineRef.current) group.addLayer(polylineRef.current);

    const b = group.getBounds();

    if (b.isValid()) {
      map.fitBounds(b.pad(0.15));
      toast("Track fitted to map", { icon: "📍" });
    } else {
      toast("No track data available", { icon: "⚠️" });
    }
  };

  const recenter = () => {
    if (mapRef.current && latestLatLngRef.current) {
      mapRef.current.panTo(latestLatLngRef.current, {
        animate: true,
        duration: 0.5
      });
      toast("Recenter to latest position", { icon: "📍" });
    } else {
      toast("No live position available", { icon: "⚠️" });
    }
  };

  useEffect(() => {
    followRef.current = followLive;
  }, [followLive]);

  useEffect(() => {
    if (loading) return;
    if (mapRef.current) return;

    const map = L.map(mapBoxRef.current).setView(DEFAULT_CENTER, 5);
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap"
    }).addTo(map);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);

      map.remove();
      mapRef.current = null;
    };
  }, [loading]);

  useEffect(() => {
    if (loading) return;

    refresh();

    if (pollRef.current) {
      clearInterval(pollRef.current);
    }

    pollRef.current = setInterval(refresh, POLL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loading, refresh]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading sentinel escort live tracking...
        </p>
      </div>
    );
  }

  return (
    <Layout
      routePage={routePage}
      dashboardData={dashboardData}
      activePage="escort"
      activeMenu="escort"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
        }}
      />

      <style>{`
        .leaflet-container {
          height: 100%;
          width: 100%;
        }

        .escort-vehicle-icon {
          background: transparent;
          border: none;
        }

        .stopped-marker {
          background: transparent;
          border: none;
        }

        /* Dark theme overrides */
        .bg-white { background: var(--card-bg); }
        .border { border-color: var(--card-border); }
        .border-b { border-bottom-color: var(--divider); }
        .bg-slate-50 { background: var(--surface); }
        .text-slate-500 { color: var(--text-muted); }
        .text-slate-700 { color: var(--text-secondary); }
        .text-slate-900 { color: var(--text-primary); }
        .border-slate-200 { border-color: var(--surface-border); }
        .bg-red-50 { background: var(--red-faint); }
        .text-red-700 { color: var(--red); }
        .shadow-sm { box-shadow: 0 8px 32px rgba(0,0,0,0.3); }
        .rounded-2xl { border-radius: 16px; }
      `}</style>

      {pageError && (
        <div className="mb-4 p-3 rounded-xl bg-[var(--red-faint)] text-[var(--red)] border border-[var(--red-border)]">
          {pageError}
        </div>
      )}

      <div className="bg-[var(--card-bg)] rounded-2xl border border-[var(--card-border)] shadow-[0_8px_32px_rgba(0,0,0,0.3)] overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--divider)] flex gap-4 items-center flex-wrap bg-[var(--card-bg)]">
          <h3 className="font-extrabold text-base m-0 text-[var(--text-primary)]">
            Sentinel Escort Live Tracking
          </h3>

          <div className="text-[var(--text-secondary)]">
            Member: <b className="text-[var(--text-primary)]">{memberId || "—"}</b>
          </div>

          {runId && (
            <div className="text-[var(--text-secondary)]">
              Run #<b className="text-[var(--text-primary)]">{runId}</b>
            </div>
          )}

          <button
            onClick={() => navigate("/sentinel_escort_list")}
            className="ml-auto px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
          >
            Escort List
          </button>

          <div
            className={`px-3 py-1.5 rounded-full text-xs font-extrabold ${status.className}`}
          >
            {status.label}
          </div>
        </div>

        <div className="px-4 py-3 flex gap-4 flex-wrap bg-[var(--surface)] border-b border-[var(--divider)]">
          <Info label="Title" value={meta.title} />
          <Info label="From" value={meta.from} />
          <Info label="To" value={meta.to} />
          <Info label="Vehicle" value={meta.vehicle} />
          <Info label="Last" value={meta.lastTs} />
        </div>

        <div className="px-4 py-3 flex gap-3 flex-wrap border-b border-[var(--divider)] items-center bg-[var(--card-bg)]">
          <button
            onClick={() => setFollowLive(v => !v)}
            className="px-4 py-2 rounded-xl text-white text-sm font-extrabold transition-transform hover:-translate-y-0.5"
            style={{ 
              background: followLive ? skin.gradient : "#64748b",
              boxShadow: followLive ? "0 4px 18px var(--accent-glow)" : "none"
            }}
          >
            {followLive ? "✓ Following Live" : "Follow Live"}
          </button>

          <button
            onClick={fitTrack}
            className="px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
          >
            Fit Track
          </button>

          <button
            onClick={recenter}
            className="px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
          >
            Recenter
          </button>

          <div className="flex-1" />

          <Info label="Distance" value={`${meta.distance} km`} />
          <Info label="Speed" value={`${meta.speed} km/h`} />
          <Info label="Duration" value={meta.duration} />
          <Info label="Points" value={meta.points} />
        </div>

        <div
          ref={mapBoxRef}
          className="h-[calc(100vh-230px)] min-h-[550px] bg-[var(--surface)]"
        />
      </div>
    </Layout>
  );
}

function Info({ label, value }) {
  return (
    <div className="text-xs text-[var(--text-muted)]">
      <span className="mr-1">
        {label}:
      </span>
      <b className="text-[var(--text-primary)]">{value || "—"}</b>
    </div>
  );
}